import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as TimeIcon,
  GraphicEq as FreqIcon,
  CheckCircle as ConfidenceIcon,
  Timeline as ActivityIcon,
} from '@mui/icons-material';
import { MinimalBirdIcon as BirdIcon } from './MinimalBirdIcon';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  TIME_SLOTS_24H,
  SPECIES_DIURNAL_PROFILES,
  SpeciesDiurnalProfile,
  DARK_THEME_EXPORTING,
  DARK_THEME_NAVIGATION,
} from '../bioacousticMockData';

interface SpeciesAvgActivityModalProps {
  open: boolean;
  onClose: () => void;
  species: {
    name: string;
    commonName?: string;
    scientificName?: string;
    family?: string;
    type?: string;
    threatLevel?: string;
    detections?: number;
    confidence?: number;
  } | null;
}

const THREAT_COLORS: Record<string, string> = {
  Endangered: '#00D4FF',
  Vulnerable: '#8B5CF6',
  'Least Concerned': '#10B981',
  Unknown: '#F97316',
};

const SpeciesAvgActivityModal: React.FC<SpeciesAvgActivityModalProps> = ({
  open,
  onClose,
  species,
}) => {
  if (!species) return null;

  const scientificName = species.scientificName || species.name;
  const profile: SpeciesDiurnalProfile = SPECIES_DIURNAL_PROFILES[scientificName] || {
    speciesName: species.commonName || species.name,
    scientificName: scientificName,
    family: species.family || 'Taxonomic Family',
    category: species.type || 'Avian',
    threatLevel: species.threatLevel || 'Least Concerned',
    totalDetections: species.detections || 95,
    avgConfidence: species.confidence || 88,
    peakWindow: '06:00 - 08:30 (Dawn Chorus)',
    peakHour: '06:00 AM',
    timeSlots: TIME_SLOTS_24H,
    hourlyActivity: [0, 0, 0, 10, 180, 140, 45, 30, 0, 0, 40, 95, 30, 15, 0, 0],
    frequencyBand: '1.5 kHz – 5.0 kHz',
    avgDecibels: -18.5,
  };

  const threatColor = THREAT_COLORS[profile.threatLevel] || '#10B981';

  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'areaspline',
      backgroundColor: 'transparent',
      height: 280,
      style: {
        fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
      },
    },
    exporting: DARK_THEME_EXPORTING,
    navigation: DARK_THEME_NAVIGATION,
    title: {
      text: '24-Hour Diurnal Vocal Activity Curve',
      align: 'left',
      style: {
        color: '#FFFFFF',
        fontSize: '13px',
        fontWeight: '700',
        letterSpacing: '0.5px',
      },
    },
    credits: { enabled: false },
    xAxis: {
      categories: profile.timeSlots,
      title: {
        text: 'Time of Day (24-Hour Continuous Cycle)',
        style: { color: '#777777', fontSize: '11px', fontWeight: '600' },
      },
      labels: {
        rotation: -45,
        style: { color: '#888888', fontSize: '10px' },
      },
      lineColor: '#222222',
      tickColor: '#222222',
      gridLineColor: 'rgba(255, 255, 255, 0.03)',
      plotBands: [
        {
          from: 3.5,
          to: 6.5,
          color: 'rgba(16, 185, 129, 0.08)',
          label: {
            text: '🌅 Dawn Chorus (Peak)',
            style: { color: '#10B981', fontSize: '10px', fontWeight: '700' },
            align: 'center',
            y: 12,
          },
        },
        {
          from: 11.5,
          to: 14.5,
          color: 'rgba(0, 212, 255, 0.05)',
          label: {
            text: '🌆 Dusk Vocalization',
            style: { color: '#00D4FF', fontSize: '10px', fontWeight: '700' },
            align: 'center',
            y: 12,
          },
        },
      ],
    },
    yAxis: {
      title: {
        text: 'Acoustic Detections / Hr',
        style: { color: '#777777', fontSize: '11px', fontWeight: '600' },
      },
      labels: {
        style: { color: '#888888', fontSize: '10px' },
      },
      min: 0,
      gridLineColor: '#1A1A1A',
    },
    tooltip: {
      backgroundColor: '#111111',
      borderColor: '#222222',
      style: { color: '#EEEEEE', fontSize: '11px' },
      shared: true,
      valueSuffix: ' vocalizations',
    },
    plotOptions: {
      areaspline: {
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, 'rgba(16, 185, 129, 0.45)'],
            [1, 'rgba(16, 185, 129, 0.01)'],
          ],
        },
        lineColor: '#10B981',
        lineWidth: 2.5,
        marker: {
          radius: 3.5,
          fillColor: '#10B981',
          lineColor: '#000',
          lineWidth: 1,
        },
      },
    },
    legend: { enabled: false },
    series: [
      {
        type: 'areaspline',
        name: `${profile.speciesName} Activity`,
        data: profile.hourlyActivity,
      },
    ],
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0E0E0E',
          backgroundImage: 'linear-gradient(180deg, rgba(20,20,20,0.95) 0%, rgba(10,10,10,0.98) 100%)',
          border: '1px solid #282828',
          borderRadius: '16px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.9), 0 0 20px rgba(16,185,129,0.1)',
          color: '#fff',
          overflow: 'hidden',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
        },
      }}
    >
      <DialogContent sx={{ p: 3.5 }}>
        {/* Top Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: 'rgba(16, 185, 129, 0.12)',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#10B981',
              }}
            >
              <BirdIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                  {profile.speciesName}
                </Typography>
                <Chip
                  label={profile.threatLevel}
                  size="small"
                  sx={{
                    bgcolor: `${threatColor}18`,
                    color: threatColor,
                    border: `1px solid ${threatColor}44`,
                    fontWeight: 700,
                    fontSize: '10px',
                    height: '20px',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#888', fontStyle: 'italic', mt: 0.25 }}>
                {profile.scientificName} • {profile.family}
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#666',
              bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px solid #222',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* 4 Metric Telemetry Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.02)',
              border: '1px solid #202020',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <TimeIcon sx={{ fontSize: 14, color: '#10B981' }} />
              <Typography sx={{ fontSize: '10px', color: '#777', textTransform: 'uppercase', fontWeight: 700 }}>
                Peak Vocal Period
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#10B981' }}>
              {profile.peakWindow.split(' (')[0]}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#555', mt: 0.25 }}>
              {profile.peakWindow.split(' (')[1]?.replace(')', '') || 'Dawn Chorus'}
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.02)',
              border: '1px solid #202020',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <ActivityIcon sx={{ fontSize: 14, color: '#00D4FF' }} />
              <Typography sx={{ fontSize: '10px', color: '#777', textTransform: 'uppercase', fontWeight: 700 }}>
                Total Detections
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#00D4FF' }}>
              {profile.totalDetections} calls
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#555', mt: 0.25 }}>
              Across 24h monitoring
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.02)',
              border: '1px solid #202020',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <ConfidenceIcon sx={{ fontSize: 14, color: '#F59E0B' }} />
              <Typography sx={{ fontSize: '10px', color: '#777', textTransform: 'uppercase', fontWeight: 700 }}>
                Avg Confidence
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#F59E0B' }}>
              {profile.avgConfidence}%
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#555', mt: 0.25 }}>
              Model detection score
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.02)',
              border: '1px solid #202020',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <FreqIcon sx={{ fontSize: 14, color: '#8B5CF6' }} />
              <Typography sx={{ fontSize: '10px', color: '#777', textTransform: 'uppercase', fontWeight: 700 }}>
                Frequency Band
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#8B5CF6' }}>
              {profile.frequencyBand}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#555', mt: 0.25 }}>
              Signal acoustic range
            </Typography>
          </Box>
        </Box>

        {/* Diurnal Highcharts Graphic */}
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.015)',
            border: '1px solid #202020',
            borderRadius: '12px',
            p: 2.5,
          }}
        >
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SpeciesAvgActivityModal;
